import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";
import * as THREE from "three";

import { ClientStatus } from "@skydio/channels/src/client_status_pb";
import { DeviceNodeHealth } from "@skydio/channels/src/device_node_health_summary_pb";
import { UiVariablesPb } from "@skydio/channels/src/phone_ui_variables_pb";
import { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import { movement_t } from "@skydio/lcm/types/planner/movement_t";
import { metersToFeet, secondsToMilliseconds } from "@skydio/math";
import { AccessoryEnum } from "@skydio/pbtypes/pbtypes/gen/accessories/accessory_enum_pb";
import { PortState } from "@skydio/pbtypes/pbtypes/gen/attachment_manager/port_state_pb";
import { ClientType } from "@skydio/pbtypes/pbtypes/gen/cloud_config/client_type_pb";
import { VehicleFault } from "@skydio/pbtypes/pbtypes/gen/common/fault_pb";
import { FeatureFlagEnum } from "@skydio/pbtypes/pbtypes/gen/flight_deck/feature_flag_enum_pb";
import { MediaMode } from "@skydio/pbtypes/pbtypes/gen/flight_deck/media_mode_pb";
import { TakeoffType } from "@skydio/pbtypes/pbtypes/gen/flight_deck/takeoff_type_pb";
import { PublicFault } from "@skydio/pbtypes/pbtypes/gen/health_monitor/public_fault_pb";
import { KeyValue } from "@skydio/pbtypes/pbtypes/gen/skills/key_value_pb";
import { UiVariableKeys } from "@skydio/pbtypes/pbtypes/gen/skills/ui_variable_keys_pb";
import { DeviceLinksSummary } from "@skydio/pbtypes/pbtypes/infrastructure/flight_connect/link_summary_pb";
import { MissionState } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission_pb";
import { FreeLookMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";
import { makeWrappedSelectors } from "@skydio/redux_util/src";
import { LinearCameraModel } from "@skydio/shared_ui/components/three/camera/linear_camera/linear_camera_model";
import { convertTransPbToObject3 } from "@skydio/shared_ui/utils/math";
import { WebRTCConnectionState } from "@skydio/skybus/src/transport/types";

import { getIsTracking } from "state/ar/selectors";
import { getCurrentUserId } from "state/current_user/selectors";
import { getFeatureFlagValue } from "state/featureFlags";
import { getCurrentPose } from "state/flight_player/selectors";
import {
  getIsRunningMission,
  getMissionState,
  getSupportedFreeLookMode,
} from "state/mission/selectors";
import { getZoomFeedback } from "state/ui_feedback/selectors";
import { getCurrentEnableRapidDescent } from "state/vehicle_settings/selectors";
import { boolToNum, kMilliTol } from "utils/common";

import * as partialSelectors from "./partialSelectors";
import { CLOUD_SKILLS } from "./types";
import {
  calculateDesiredPose,
  calculateImuTCamHeadingPitch,
  calculateNavHeadingPitch,
  calculateNavTCommand,
  clampVelocityToVehicleLimits,
  convertFluToRdf,
  DESIRED_POSE_TIMEOUT,
  getFlightStateName,
  getGamepadMovementVector,
  getKeyboardMovementVector,
  isSkillSupported,
  MAX_KEYBOARD_PITCH_RATE,
  MAX_KEYBOARD_YAW_RATE,
  MOUSE_COMMAND_TIMEOUT,
  TAP_DURATION,
} from "./utils";

import type { ValueType } from "@skydio/core";
import type { AppState } from "state/store";
import type { MovementDirection, TeleopAction } from "./types";

export const getPilotSlice = (state: AppState) => state.pilot;
export const isVehicleConnected = (state: AppState) => state.pilot.vehicleConnection.success;
export const getMostRecentPilotUpdatedAt = (state: AppState) =>
  state.pilot.mostRecentPilot.updatedAt;
export const getIsSignaling = (state: AppState) =>
  state.pilot.vehicleConnection.status === WebRTCConnectionState.SIGNALING;

// keyboard speed related selectors
export const getVehicleSpeedSettings = (state: AppState) => state.pilot.vehicleSpeedSettings;
export const getMaxVehicleTranslationSpeed = (state: AppState) =>
  state.pilot.vehicleSpeedSettings.max;
export const getMinVehicleTranslationSpeed = (state: AppState) =>
  state.pilot.vehicleSpeedSettings.min;
export const getNominalVehicleTranslationSpeed = (state: AppState) =>
  state.pilot.vehicleNominalTranslationSpeed;
export const getDesiredNominalVehicleTranslationSpeed = (state: AppState) =>
  state.pilot.desiredVehicleNominalTranslationSpeed;
export const getDesiredZoomLevel = (state: AppState) => state.pilot.desiredZoomLevel;
export const getZoomCorrectionFactor = (state: AppState) => state.pilot.zoomCorrectionFactor;
export const getSpeedChangeTime = (state: AppState) => state.pilot.vehicleSpeedChangeTime;
export const getIsBoostActive = (state: AppState) =>
  getFeatureFlagValue(state, "teleopBoostMode") && state.pilot.boostModeActive;
export const getIsCrawlActive = (state: AppState) =>
  getFeatureFlagValue(state, "teleopCrawlMode") && state.pilot.crawlModeActive;

// account for boost and crawl
// This is the keyboard speed setting that is used for movement
export const getCorrectedNominalVehicleTranslationSpeed = createSelector(
  getNominalVehicleTranslationSpeed,
  getMaxVehicleTranslationSpeed,
  getMinVehicleTranslationSpeed,
  getIsBoostActive,
  getIsCrawlActive,
  (nominalTranslationSpeed, maxTranslationSpeed, minTranslationSpeed, boostActive, crawlActive) => {
    if (boostActive) return maxTranslationSpeed;
    if (crawlActive) return minTranslationSpeed;
    return nominalTranslationSpeed;
  }
);

// translation to yaw multiplier accounting for speed settings, zoom, boost, and crawl.
const getTranslationToYawFactor = createSelector(
  getVehicleSpeedSettings,
  getZoomCorrectionFactor,
  getIsBoostActive,
  getIsCrawlActive,
  (speedSettings, zoomCorrectionFactor, boostActive, crawlActive) => {
    if (boostActive || crawlActive) return speedSettings.translationToYaw;
    return speedSettings.translationToYaw * zoomCorrectionFactor;
  }
);
export const getNominalVehicleYawRate = createSelector(
  getCorrectedNominalVehicleTranslationSpeed,
  getTranslationToYawFactor,
  (desiredSpeed, translationToYaw) => {
    return desiredSpeed * translationToYaw;
  }
);
export const getMaxVehicleYawRate = createSelector(
  getMaxVehicleTranslationSpeed,
  getTranslationToYawFactor,
  (maxSpeed, translationToYaw) => {
    return maxSpeed * translationToYaw;
  }
);

// translation to pitch multiplier accounting for speed settings, zoom, boost, and crawl.
const getTranslationToPitchFactor = createSelector(
  getVehicleSpeedSettings,
  getZoomCorrectionFactor,
  getIsBoostActive,
  getIsCrawlActive,
  (speedSettings, zoomCorrectionFactor, boostActive, crawlActive) => {
    if (boostActive || crawlActive) return speedSettings.translationToPitch;
    return speedSettings.translationToPitch * zoomCorrectionFactor;
  }
);
export const getNominalGimbalPitchRate = createSelector(
  getCorrectedNominalVehicleTranslationSpeed,
  getTranslationToPitchFactor,
  (desiredSpeed, translationToPitch) => desiredSpeed * translationToPitch
);
export const getMaxGimbalPitchRate = createSelector(
  getMaxVehicleTranslationSpeed,
  getTranslationToPitchFactor,
  (maxSpeed, translationToPitch) => maxSpeed * translationToPitch
);

export const {
  getClientId,
  getMostRecentPilotId,
  getIsMostRecentVehiclePilot,
  wasCommandeerRequested,
  getCurrentFlightState,
  isResting,
  isReadyForTakeoff,
  isTakingOff,
  isLanding,
  isReturningToDock,
  isReturningToLaunch,
  isInFlight,
  isAbortingLanding,
  isInPostFlight,
  isInFlyingUnknownState,
  isLandedInDockAndDockIsClosing,
  isWaitingForTakeoff,
  isReturningToSafeLanding,
} = makeWrappedSelectors(partialSelectors, getPilotSlice);

export const getHasReceivedPilot = createSelector(
  [getMostRecentPilotUpdatedAt],
  updatedAt => updatedAt !== -1
);

export const getCanSwitchRunmode = createSelector(
  isResting,
  isReadyForTakeoff,
  (resting, readyForTakeoff) => resting || readyForTakeoff
);

export const getCurrentFlightStateName = createSelector(getCurrentFlightState, flightState =>
  flightState ? getFlightStateName(flightState) : ""
);

export const wasTakeoffRequested = (state: AppState) => state.pilot.takeoffRequested;

export const isVehicleMovingAutonomously = (state: AppState) => state.pilot.movingAutonomously;

export const getPolicyCommandType = (state: AppState) => state.pilot.policyCommandType;

export const isAutonomyOff = (state: AppState) => state.pilot.isAutonomyOff;

export const isInManualTeleop = createSelector(
  isInFlight,
  getIsTracking,
  isVehicleMovingAutonomously,
  getIsRunningMission,
  (flying, tracking, isFlyingAutonomously, isFlyingMission) => {
    return flying && !tracking && !isFlyingAutonomously && !isFlyingMission;
  }
);

// display settings
export const isPointerLocked = (state: AppState) => state.pilot.mouseInput.locked;
export const getDisplayPointerLockInnerRing = (state: AppState) =>
  state.pilot.displayPointerLockInnerRing;
export const getDisplayPreciseTeleopARSetting = (state: AppState) =>
  state.pilot.displayPreciseTeleopAr;
export const getDesiredDisplayPreciseTeleopARSetting = (state: AppState) =>
  state.pilot.desiredDisplayPreciseTeleopAr;

const getMovementKeybindings = (state: AppState) => state.pilot.movementKeybindings;
const getTeleopActionKeybindings = (state: AppState) => state.pilot.teleopActionKeybindings;
const getAllKeybindings = createSelector(
  getMovementKeybindings,
  getTeleopActionKeybindings,
  (movementKeybindings, teleopActionKeybindings) => {
    const allKeybindings = {} as Record<MovementDirection | TeleopAction, string>;
    Object.assign(allKeybindings, movementKeybindings, teleopActionKeybindings);
    return allKeybindings;
  }
);
export const canConfigureObstacleMargin = createSelector(
  isVehicleMovingAutonomously,
  movingAutonomously => !movingAutonomously
);
const executiveActionHotkeysAvailable = (state: AppState) =>
  getFeatureFlagValue(state, "executiveActionHotkeys");
const missionsV2Active = (state: AppState) => getFeatureFlagValue(state, "liveMissionPlanning");
const boostModeAvailable = (state: AppState) => getFeatureFlagValue(state, "teleopBoostMode");
const crawlModeAvailable = (state: AppState) => getFeatureFlagValue(state, "teleopCrawlMode");
export const getKeybindings = createSelector(
  getAllKeybindings,
  canConfigureObstacleMargin,
  executiveActionHotkeysAvailable,
  boostModeAvailable,
  crawlModeAvailable,
  missionsV2Active,
  (
    allKeybindings,
    includeObstacleKey,
    includeExecutiveActions,
    includeBoostMode,
    includeCrawlMode,
    missionsV2Active
  ) => {
    const keysToExclude = [];
    // these are the action keys, Live Missions V2 doesn't use Redux, so don't lock those down here
    if (missionsV2Active) keysToExclude.push("b", "n");
    if (!includeObstacleKey) keysToExclude.push("toggleObstacleAvoidance");
    if (!includeExecutiveActions) keysToExclude.push("returnToDock", "cancel");
    if (!includeBoostMode) keysToExclude.push("boost");
    if (!includeCrawlMode) keysToExclude.push("crawl");
    return _.omit(allKeybindings, keysToExclude);
  }
);

// don't want to show workflow keys since they're included in the skill UI (e.g mission planning)
export const getKeybindingsForDisplay = createSelector(getKeybindings, bindings =>
  _.omit(bindings, ["workflowNext", "workflowPrevious", "returnToDock", "cancel"])
);

export const getValidInputKeys = createSelector(getKeybindings, bindings => _.values(bindings));
export const getCurrentKeyboardMovementInputs = (state: AppState) =>
  state.pilot.keyboardMovementInputs;
export const getIsKeyboardPositionCommand = (state: AppState) => state.pilot.positionCommand;
export const getIsKeyboardVelocityCommand = (state: AppState) => state.pilot.velocityCommand;
export const getCurrentTeleopActionInputs = (state: AppState) => state.pilot.teleopActionInputs;
export const getCurrentTeleopActionSources = (state: AppState) => state.pilot.teleopActionSources;
export const getCurrentActiveTeleopActionInputs = createSelector(
  getCurrentTeleopActionInputs,
  actionInputs => {
    // get all inputs with positive utimes
    return _.pickBy(actionInputs, input => input > 0) as { [k in TeleopAction]?: number };
  }
);
export const getCurrentMouseOrientation = (state: AppState) => state.pilot.mouseInput.orientation;
export const getCurrentMouseInput = (state: AppState) => state.pilot.mouseInput;
export const getLastMouseMoveTimestamp = (state: AppState) => state.pilot.mouseInput.lastMouseMove;
export const getCurrentButtonInputs = (state: AppState) => state.pilot.buttonInputs;
export const isGamepadConnected = (state: AppState) => state.pilot.gamepadConnected;
export const getGamepadMapping = (state: AppState) => state.pilot.gamepadMapping;
export const getCurrentGamepadSettings = (state: AppState) => state.pilot.gamepadSettings;
export const getDesiredGamepadSettings = (state: AppState) => state.pilot.desiredGamepadSettings;
export const getCurrentGamepadMode = createSelector(
  getCurrentGamepadSettings,
  settings => settings.mode
);
export const getCurrentGamepadExpoLevel = createSelector(
  getCurrentGamepadSettings,
  settings => settings.expoLevel
);
export const getCurrentGamepadThrottleSensitivity = createSelector(
  getCurrentGamepadSettings,
  settings => settings.throttleSensitivity
);
export const getCurrentGamepadYawSensitivity = createSelector(
  getCurrentGamepadSettings,
  settings => settings.yawSensitivity
);
export const getCurrentGamepadPitchSensitivity = createSelector(
  getCurrentGamepadSettings,
  settings => settings.pitchSensitivity
);
export const getDesiredGamepadMode = createSelector(
  getDesiredGamepadSettings,
  settings => settings.mode
);
export const getDesiredGamepadExpoLevel = createSelector(
  getDesiredGamepadSettings,
  settings => settings.expoLevel
);
export const getDesiredGamepadThrottleSensitivity = createSelector(
  getDesiredGamepadSettings,
  settings => settings.throttleSensitivity
);
export const getDesiredGamepadYawSensitivity = createSelector(
  getDesiredGamepadSettings,
  settings => settings.yawSensitivity
);
export const getDesiredGamepadPitchSensitivity = createSelector(
  getDesiredGamepadSettings,
  settings => settings.pitchSensitivity
);
export const getCurrentGamepadMovementInputs = (state: AppState) =>
  state.pilot.gamepadMovementInputs;
export const getFlightId = (state: AppState) => state.pilot.flightId;
export const getFlightPhaseId = (state: AppState) => state.pilot.flightPhaseId;

export const getKeyboardVelocityVector = createSelector(
  getCurrentKeyboardMovementInputs,
  getCorrectedNominalVehicleTranslationSpeed,
  (keyboard, maxSpeed) => {
    return getKeyboardMovementVector(keyboard)
      .clone()
      .multiplyScalar(maxSpeed)
      .clampLength(0, maxSpeed);
  }
);
export const getKeyboardPositionVector = createSelector(getKeyboardVelocityVector, velocity =>
  velocity.clone().multiplyScalar(TAP_DURATION)
);

export const getKeyboardHeadingRate = createSelector(
  getCurrentKeyboardMovementInputs,
  getNominalVehicleYawRate,
  (keyboard, maxYawRate) => {
    return _.clamp(
      (boolToNum(keyboard.yawLeft > 0) - boolToNum(keyboard.yawRight > 0)) * maxYawRate,
      -MAX_KEYBOARD_YAW_RATE,
      MAX_KEYBOARD_YAW_RATE
    );
  }
);
export const getKeyboardHeadingIncrement = createSelector(
  getKeyboardHeadingRate,
  yawRate => yawRate * TAP_DURATION
);

export const getKeyboardPitchRate = createSelector(
  getCurrentKeyboardMovementInputs,
  getNominalGimbalPitchRate,
  (keyboard, maxPitchRate) => {
    return _.clamp(
      (boolToNum(keyboard.gimbalUp > 0) - boolToNum(keyboard.gimbalDown > 0)) * maxPitchRate,
      -MAX_KEYBOARD_PITCH_RATE,
      MAX_KEYBOARD_PITCH_RATE
    );
  }
);
export const getKeyboardPitchIncrement = createSelector(
  getKeyboardPitchRate,
  pitchRate => pitchRate * TAP_DURATION
);

export const getGamepadVelocityVector = createSelector(
  getCurrentGamepadMovementInputs,
  getMaxVehicleTranslationSpeed,
  getCurrentGamepadThrottleSensitivity,
  getIsBoostActive,
  getIsCrawlActive,
  (gamepad, maxSpeed, throttleSensitivity, boostActive, crawlActive) => {
    const multiplier = boostActive ? 1.0 : crawlActive ? 0.1 : throttleSensitivity.value;
    return getGamepadMovementVector(gamepad)
      .clone()
      .multiplyScalar(maxSpeed * multiplier)
      .clampLength(0, maxSpeed * multiplier);
  }
);
export const getGamepadHeadingRate = createSelector(
  getCurrentGamepadMovementInputs,
  getMaxVehicleYawRate,
  getCurrentGamepadYawSensitivity,
  getIsBoostActive,
  getIsCrawlActive,
  (gamepad, maxYawRate, yawSensitivity, boostActive, crawlActive) => {
    const multiplier = boostActive ? 1.0 : crawlActive ? 0.1 : yawSensitivity.value;
    return (gamepad.yawLeft - gamepad.yawRight) * maxYawRate * multiplier;
  }
);
export const getGamepadPitchRate = createSelector(
  getCurrentGamepadMovementInputs,
  getMaxGimbalPitchRate,
  getCurrentGamepadPitchSensitivity,
  getIsBoostActive,
  getIsCrawlActive,
  (gamepad, maxPitchRate, pitchSensitivity, boostActive, crawlActive) => {
    const multiplier = boostActive ? 1.0 : crawlActive ? 0.1 : pitchSensitivity.value;
    return (gamepad.gimbalUp - gamepad.gimbalDown) * maxPitchRate * multiplier;
  }
);

export const getWebrtcClientId = (state: AppState) => state.pilot.vehicleConnection.clientId;
export const getSessionId = (state: AppState) => state.pilot.sessionId;
export const getRunmode = (state: AppState) => state.pilot.runmode;
export const getConfigReceived = (state: AppState) => state.pilot.configReceived;

export const disableSkillSelector = createSelector(
  isTakingOff,
  isLanding,
  isReturningToDock,
  isReturningToLaunch,
  getRunmode,
  (takingOff, landing, returningToDock, returningToLaunch, runmode) =>
    takingOff || landing || returningToDock || returningToLaunch || !runmode
);

export const getImuTCamEOPb = (state: AppState) => state.pilot.eo_imu_T_cam;
export const getImuTCamIRPb = (state: AppState) => state.pilot.ir_imu_T_cam;
export const getImuTCamIR = createSelector(getImuTCamIRPb, imuTCamIR => {
  if (imuTCamIR) {
    return convertTransPbToObject3(imuTCamIR);
  }
  // identity as a fallback
  return new THREE.Object3D();
});

const getGimbalCamRImu = (state: AppState) => state.pilot.gimbal_cam_R_imu;
export const getImuRGimbalCamRdf = createSelector(getGimbalCamRImu, gimbalCamRImu =>
  gimbalCamRImu ? convertFluToRdf(gimbalCamRImu.clone().invert()) : undefined
);

export const getImuTCamPb = (state: AppState) => state.pilot.imu_T_subject_cam;

export const getImuTCamEO = createSelector(getImuTCamPb, getImuTCamEOPb, (imuTCam, imuTCamEO) => {
  if (imuTCamEO) {
    return convertTransPbToObject3(imuTCamEO);
  } else if (imuTCam) {
    return convertTransPbToObject3(imuTCam);
  }
  // identity as a fallback
  return new THREE.Object3D();
});

export const getNavTGimbalCamImuPb = (state: AppState) =>
  state.pilot.nav_T_gimbal_camera_imu?.transform;
export const getNavTGimbalCamImu = createSelector(getNavTGimbalCamImuPb, navTGimbalCamImu =>
  navTGimbalCamImu ? convertTransPbToObject3(navTGimbalCamImu) : undefined
);

// If nav_t_cam is stale, position commands will be referenced of an old vehicle pose and so will be
// inaccurate. This resolves SW-49492. Reduced from 5000 to 1000 following CS-11785.
const TRANSFORM_STALE_THRESHOLD = 1000; // ms
const isNavTGimbalCamImuStale = (state: AppState) =>
  state.pilot.nav_T_gimbal_camera_imu
    ? performance.now() - state.pilot.nav_T_gimbal_camera_imu.timestamp > TRANSFORM_STALE_THRESHOLD
    : false;

// NOTE (divyanshu): extracting individual components to force the selector to listen to changes in
// the Vector3 objects in state.pilot.vehiclePosition and state.pilot.vehicleVelocity
const vehiclePosition = (state: AppState) =>
  new THREE.Vector3(
    state.pilot.vehiclePosition?.x,
    state.pilot.vehiclePosition?.y,
    state.pilot.vehiclePosition?.z
  );
export const getVehiclePosition = createSelector(vehiclePosition, position => position);
const vehicleTraslationalVelocity = (state: AppState) =>
  new THREE.Vector3(
    state.pilot.vehicleVelocity?.x,
    state.pilot.vehicleVelocity?.y,
    state.pilot.vehicleVelocity?.z
  );
export const getVehicleSpeed = createSelector(vehicleTraslationalVelocity, velocity => {
  return velocity?.length();
});
export const getDesiredPose = (state: AppState) => state.pilot.desiredPose;

export const getConnectionStats = createSelector(
  (state: AppState) => state.pilot.vehicleConnection,
  ({ downloadBitrate, uploadBitrate }) => ({ uploadBitrate, downloadBitrate })
);

export const getActiveFaults = (state: AppState) => state.pilot.activeFaults;

export const getBlockingPrompt = (state: AppState) => state.pilot.blockingPrompt;

export const hasBlockingPrompt = (state: AppState) =>
  (state.pilot.blockingPrompt?.title?.length ?? 0) > 0;

const createFaultActiveSelector = (fault: number) =>
  createSelector(getActiveFaults, faults => faults.includes(fault));
export const isWaitingForCamera = createFaultActiveSelector(
  PublicFault.Enum.SUBJECT_CAMERA_UNRELIABLE
);
export const isLowBatteryRtdActive = createFaultActiveSelector(
  PublicFault.Enum.BATTERY_LOW_RETURN_TO_DOCK
);

export const isLowBatteryReturnActive = createFaultActiveSelector(
  VehicleFault.Enum.BATTERY_LOW_RETURN
);

export const isBatteryCriticalActive = createFaultActiveSelector(
  VehicleFault.Enum.BATTERY_CRITICAL
);

export const isGimbalFlashlightTooHot = createFaultActiveSelector(
  VehicleFault.Enum.GIMBAL_FLASHLIGHT_TOO_HOT
);

export const isVehicleReturning = createSelector(
  isReturningToDock,
  isReturningToLaunch,
  isReturningToSafeLanding,
  (returningToDock, returningToLaunch, returningToSafeLanding) =>
    returningToDock || returningToLaunch || returningToSafeLanding
);

export const isVehicleAirborne = createSelector(
  isInFlight,
  isTakingOff,
  isLanding,
  isAbortingLanding,
  isVehicleReturning,
  (isFlying, takingOff, landing, abortingLanding, isReturning) =>
    isFlying || takingOff || landing || abortingLanding || isReturning
);

export const getActiveSkill = (state: AppState) => state.pilot.skillState;
export const getIsWaypointsSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.WAYPOINTS
);
export const getIsManualSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.MANUAL
);
export const getIsMissionPlannerSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.MISSION_PLANNER
);

// If we are airborne, then we support all cloud skills. If on the ground, only support the ground
// compatible skills.
export const getIsActiveSkillSupported = createSelector(
  isVehicleAirborne,
  getActiveSkill,
  (airborne, activeSkill) => isSkillSupported(activeSkill, airborne)
);

const DISALLOWED_MINIMUM_OA_SKILLS = [CLOUD_SKILLS.MISSION_PLANNER, CLOUD_SKILLS.SITE_MAPPER];

export const cannotUseMinimumOA = (skill: CLOUD_SKILLS | string | undefined) =>
  DISALLOWED_MINIMUM_OA_SKILLS.includes(skill as CLOUD_SKILLS);

export const getCanUseMinimumOA = createSelector(
  getActiveSkill,
  skill => !cannotUseMinimumOA(skill)
);

export const getClientStatus = createSelector(
  wasTakeoffRequested,
  isVehicleAirborne,
  getSessionId,
  getWebrtcClientId,
  getCurrentUserId,
  getConfigReceived,
  (takeoffRequested, flying, sessionId, clientId, userId, configReceived) => {
    const clientStatus = new ClientStatus();
    // Currently, vehicle is set up to only send config once per session
    // Setting session id, basically acknowledges that we have received the config
    // and it doesn't need to be sent again
    if (configReceived) {
      clientStatus.setSessionId(sessionId);
    }
    clientStatus.setClientId(clientId);
    clientStatus.setCloudUserId(userId);
    clientStatus.setWouldAcceptPilot(true);
    clientStatus.setInForeground(true);
    clientStatus.setClientType(ClientType.Enum.WEB);
    // Set Takeoff Type for backwards compatibility with old vehicle software that expects this
    // field to be set
    clientStatus.setTakeoffType(TakeoffType.Enum.DEPRECATED_HAND_TAKEOFF);
    clientStatus.addFeatureFlags(FeatureFlagEnum.Enum.GIMBAL_INSPECTION_MODE);

    if (takeoffRequested || flying) {
      clientStatus.setMediaMode(MediaMode.Enum.FLIGHT_CONTROL);
    } else {
      clientStatus.setMediaMode(MediaMode.Enum.FLEXIBLE);
    }
    return clientStatus;
  }
);

export const getCurrentObstacleSafety = (state: AppState) => state.pilot.obstacleSetting?.safety;
export const getCurrentObstacleMarginMode = (state: AppState) =>
  state.pilot.obstacleSetting?.mode ?? VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES;

export const getNavTCommand = createSelector(
  getNavTGimbalCamImu,
  getImuTCamEO,
  getImuRGimbalCamRdf,
  isNavTGimbalCamImuStale,
  calculateNavTCommand
);

export const getCamNavHeadingPitch = createSelector(getNavTCommand, calculateNavHeadingPitch);
export const getGimbalImuNavHeadingPitch = createSelector(
  getNavTGimbalCamImu,
  calculateNavHeadingPitch
);
export const getGimbalImuToCamHeadingPitch = createSelector(
  getImuTCamEO,
  calculateImuTCamHeadingPitch
);

export const isVelocityCommanded = (state: AppState) => state.pilot.velocityCommand;

// movement commands are relative to the EO camera.
export const getDesiredPoseFromKeyboardInput = createSelector(
  getKeyboardPositionVector,
  getKeyboardHeadingIncrement,
  getKeyboardPitchIncrement,
  isPointerLocked,
  getVehiclePosition,
  getCamNavHeadingPitch,
  calculateDesiredPose
);

const getCameraIntrinsics = (state: AppState) => state.pilot.cameraIntrinsics;
export const getCameraCal = createSelector(
  getCameraIntrinsics,
  getZoomFeedback,
  (cameraIntrinsics, zoomFeedback) => {
    if (
      cameraIntrinsics?.focalLength === undefined ||
      cameraIntrinsics?.principalPoint === undefined ||
      zoomFeedback.zoomLevel === undefined
    ) {
      return undefined;
    }
    const zoomLevel = zoomFeedback.zoomLevel;
    // construct a camera with the effective focal length that takes into account the zoom level
    // NOTE(divyanshu): STREAM_CAMERA_CALIBRATION_PB will have the effective focal length and so
    // adjusting for zoom will no longer be needed when we have this message
    const focalLength = new THREE.Vector2()
      .fromArray(cameraIntrinsics.focalLength.dataList)
      .multiplyScalar(zoomLevel);
    const principalPoint = new THREE.Vector2().fromArray(cameraIntrinsics.principalPoint.dataList);
    // NOTE(divyanshu): `STREAM_CAMERA_CALIBRATION_PB` will have the correct image size to use
    const imageSize = principalPoint.clone().multiplyScalar(2);
    const camera = new LinearCameraModel(focalLength, principalPoint, imageSize);
    return camera;
  }
);

// Selectors for Click To Fly
export const isClickToFlyEnabled = (state: AppState) => state.pilot.clickToFly.enabled;
export const isClickToFlyDesired = (state: AppState) => state.pilot.clickToFly.desired;
export const isClickToFlyActive = (state: AppState) => state.pilot.clickToFly.active > 0;
export const clickToFlyActiveTime = (state: AppState) => state.pilot.clickToFly.active;
export const clickToFlyClickStart = (state: AppState) => state.pilot.clickToFly.clickStart;
export const clickToFlyClickEnd = (state: AppState) => state.pilot.clickToFly.clickEnd;
export const clickToFlyScrollEnd = (state: AppState) => state.pilot.clickToFly.scrollEnd;
export const getClickToFlyRay = createSelector(
  clickToFlyClickStart,
  clickToFlyScrollEnd,
  (start, end) => {
    return { start: start, end: end };
  }
);
export const getClickToFlyDisplayTrail = (state: AppState) => state.pilot.clickToFly.displayTrail;

// Determine if movement command timed out
const RESET_GIMBAL_PITCH_TIMEOUT = 5000; // we want the position command to have ehough time for the gimbal to reset to 0
const isDesiredPoseTimedOut = (state: AppState) =>
  state.pilot.desiredPose.pitch === 0 && state.pilot.desiredPose.timestamp
    ? performance.now() - state.pilot.desiredPose.timestamp > RESET_GIMBAL_PITCH_TIMEOUT
    : state.pilot.desiredPose.timestamp
      ? performance.now() - state.pilot.desiredPose.timestamp >
        secondsToMilliseconds(DESIRED_POSE_TIMEOUT)
      : true;
const isMouseInputTimedOut = (state: AppState) =>
  performance.now() - state.pilot.mouseInput.lastMouseMove >
  secondsToMilliseconds(MOUSE_COMMAND_TIMEOUT);

export const isRapidDescentActive = createSelector(
  getIsBoostActive,
  getCurrentObstacleSafety,
  getCurrentEnableRapidDescent,
  (boostActive, obstacleSafetyValue, rapidDescentEnabled) => {
    // If obstacleSafetyValue is a number > 0, obstacle avoidance is ON
    const obstacleAvoidanceOn = typeof obstacleSafetyValue === "number" && obstacleSafetyValue > 0;
    return boostActive && obstacleAvoidanceOn && rapidDescentEnabled;
  }
);

// Selectors for Consolidated Movement Commands
const getClickToFlyCommand = createSelector(isClickToFlyActive, getClickToFlyRay, (active, ray) =>
  active ? ray : undefined
);
const getPositionCommand = createSelector(
  getDesiredPose,
  isDesiredPoseTimedOut,
  (desiredPose, desiredPoseTimedOut) => (desiredPoseTimedOut ? undefined : desiredPose.position)
);
const getVelocityCommand = createSelector(
  getKeyboardVelocityVector,
  getGamepadVelocityVector,
  isRapidDescentActive,
  (keyboardVelocity, gamepadVelocity, rapidDescentActive) => {
    const velocity = clampVelocityToVehicleLimits(
      gamepadVelocity.length() > 0 ? gamepadVelocity : keyboardVelocity,
      rapidDescentActive
    );
    const source = gamepadVelocity.length() > 0 ? "gamepad" : "keyboard";
    return { velocity, source };
  }
);
const getHeadingCommand = createSelector(
  getDesiredPose,
  isDesiredPoseTimedOut,
  isPointerLocked,
  getCurrentMouseInput,
  isMouseInputTimedOut,
  (desiredPose, desiredPoseTimedOut, pointerLocked, mouseInput, mouseInputTimedOut) => {
    if (pointerLocked) {
      return mouseInputTimedOut ? undefined : mouseInput.orientation.heading;
    }
    return desiredPoseTimedOut ? undefined : desiredPose.heading;
  }
);
const getHeadingRateCommand = createSelector(
  getKeyboardHeadingRate,
  getGamepadHeadingRate,
  isPointerLocked,
  (keyboardHeadingRate, gamepadHeadingRate, pointerLocked) => {
    // Don't allow heading rate when pointer locked
    if (pointerLocked) {
      return { headingRate: 0.0, source: "keyboard" };
    }
    const headingRate = Math.abs(gamepadHeadingRate) > 0 ? gamepadHeadingRate : keyboardHeadingRate;
    const source = Math.abs(gamepadHeadingRate) > 0 ? "gamepad" : "keyboard";
    return { headingRate: headingRate, source: source };
  }
);
const getPitchCommand = createSelector(
  getDesiredPose,
  isDesiredPoseTimedOut,
  getCurrentMouseInput,
  isMouseInputTimedOut,
  (desiredPose, desiredPoseTimedOut, mouseInput, mouseInputTimedOut) => {
    if (desiredPose.pitch !== undefined) {
      return desiredPoseTimedOut ? undefined : desiredPose.pitch;
    }
    // NOTE(divyanshu): The pitch mouse input is also used by the keyboard / gamepad to set pitch
    // since we don't support sending pitch rates. The integration is done in the main loop in
    // MovementManager.
    return mouseInputTimedOut ? undefined : mouseInput.orientation.pitch;
  }
);

// Called to set the Movement Command by MovementManager
export const getUiVariables = createSelector(
  getClickToFlyCommand,
  getPositionCommand,
  getVelocityCommand,
  getHeadingCommand,
  getHeadingRateCommand,
  getPitchCommand,
  getCorrectedNominalVehicleTranslationSpeed,
  getMaxVehicleTranslationSpeed,
  getIsBoostActive,
  getCurrentButtonInputs,
  (
    clickToFlyCommand,
    positionCommand,
    velocityCommand,
    headingCommand,
    headingRateCommand,
    pitchCommand,
    nominalVehicleTranslationalSpeed,
    maxVehicleTranslationSpeed,
    isBoostActive,
    buttons
  ) => {
    const uiVariables = new UiVariablesPb();

    const addVariable = (key: ValueType<UiVariableKeys.EnumMap>, value: number) => {
      const keyVal = new KeyValue();
      keyVal.setKey(key);
      keyVal.setValue(value);
      uiVariables.addVars(keyVal);
    };

    uiVariables.setBoostCommanded(isBoostActive);

    if (clickToFlyCommand) {
      addVariable(UiVariableKeys.Enum.RAY_START_X, clickToFlyCommand.start.x);
      addVariable(UiVariableKeys.Enum.RAY_START_Y, clickToFlyCommand.start.y);
      addVariable(UiVariableKeys.Enum.RAY_START_Z, clickToFlyCommand.start.z);
      addVariable(UiVariableKeys.Enum.RAY_END_X, clickToFlyCommand.end.x);
      addVariable(UiVariableKeys.Enum.RAY_END_Y, clickToFlyCommand.end.y);
      addVariable(UiVariableKeys.Enum.RAY_END_Z, clickToFlyCommand.end.z);
      // click to fly speed should be the nominal speed set by the user
      addVariable(UiVariableKeys.Enum.MAX_SPEED, nominalVehicleTranslationalSpeed);
      addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.NAV);
      return uiVariables;
    }

    // if you have a desired_position and there is no gamepad velocity, then command one.
    // Otherwise command a velocity (from gamepad or keyboard).
    if (positionCommand !== undefined && velocityCommand.source !== "gamepad") {
      const [posX, posY, posZ] = positionCommand.toArray();
      addVariable(UiVariableKeys.Enum.POS_X, posX);
      addVariable(UiVariableKeys.Enum.POS_Y, posY);
      addVariable(UiVariableKeys.Enum.POS_Z, posZ);
      addVariable(UiVariableKeys.Enum.MAX_SPEED, maxVehicleTranslationSpeed);
      addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.NAV);
    } else {
      if (velocityCommand.velocity.length() > kMilliTol) {
        const [velX, velY, velZ] = velocityCommand.velocity.toArray();
        addVariable(UiVariableKeys.Enum.VEL_X, velX);
        addVariable(UiVariableKeys.Enum.VEL_Y, velY);
        addVariable(UiVariableKeys.Enum.VEL_Z, velZ);
        addVariable(UiVariableKeys.Enum.MAX_SPEED, maxVehicleTranslationSpeed);
        addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.VEHICLE_YAW);
      }
    }

    if (headingCommand !== undefined && headingRateCommand.source !== "gamepad") {
      addVariable(UiVariableKeys.Enum.HEADING, headingCommand);
    } else {
      if (Math.abs(headingRateCommand.headingRate) > kMilliTol) {
        addVariable(UiVariableKeys.Enum.HEADING_RATE, headingRateCommand.headingRate);
      }
    }

    if (pitchCommand !== undefined) {
      addVariable(UiVariableKeys.Enum.GIMBAL_PITCH, pitchCommand);
    }

    addVariable(UiVariableKeys.Enum.SMOOTH_LOOKAT, 0.0);

    if (buttons.elevation) {
      addVariable(UiVariableKeys.Enum.ELEVATION, buttons.elevation);
    }
    if (buttons.range) {
      addVariable(UiVariableKeys.Enum.RANGE, buttons.range);
    }

    return uiVariables;
  }
);

export const getReturnToDockInfo = (state: AppState) => state.pilot.returnToDockInfo;

export const getSafeLandingInfo = (state: AppState) => state.pilot.safeLandingInfo;

export const getLatestValidTargetLandingPointId = (state: AppState) =>
  state.pilot.latestValidTargetLandingPointId;

export const getGimbalFlashlightStatus = (state: AppState) => state.pilot.gimbalFlashlightStatus;

export const isThirdPersonViewActive = (state: AppState) => state.pilot.thirdPersonViewActive;

export const getStreamCameraCalibration = (state: AppState) => state.pilot.streamCameraCalibration;

export const getLandingPadTracker = (state: AppState) => state.pilot.landingPadTracker;

export const getRssi = (state: AppState) => state.pilot.rssi;

export const getVehicleAmbassadorStatusLastUpdated = (state: AppState) =>
  state.pilot.vehicleAmbassadorStatusLastUpdated;

export const getDefaultPoiHeight = (state: AppState) => state.pilot.defaultPoiHeight;

export const getDefaultOrbitInterceptMode = (state: AppState) =>
  state.pilot.defaultOrbitInterceptMode;

export const getDefaultOrbitDirection = (state: AppState) => state.pilot.defaultOrbitDirection;

export const getDefaultPathBetweenPointsMode = (state: AppState) =>
  state.pilot.defaultPathBetweenPointsMode;

export const getDefaultLookAtPointActive = (state: AppState) =>
  state.pilot.defaultLookAtPointActive;

export const getRampHeightModeActive = (state: AppState) => state.pilot.rampHeightModeActive;

const getIsPointerLockEnabledForMissions = (state: AppState) =>
  getFeatureFlagValue(state, "enablePointerLockDuringMissionsRfd");

export const isPointerLockAllowed = createSelector(
  isInManualTeleop,
  getIsPointerLockEnabledForMissions,
  getSupportedFreeLookMode,
  isVehicleReturning,
  getMissionState,
  (
    manualTeleop,
    pointerLockEnabledForMissions,
    supportedFreeLookMode,
    vehicleReturning,
    missionState
  ) => {
    // allow pointer lock only if we're in manual teleop or running a mission, provided we have the
    // feature flag to enable pointer lock for missions
    const freeLookAllowsPanAndTilt =
      supportedFreeLookMode !== undefined &&
      supportedFreeLookMode.valueOf() === FreeLookMode_Enum.PAN_AND_TILT;
    const executingPostMissionActions = (
      [
        MissionState.MissionStateEnum.COMPLETED,
        MissionState.MissionStateEnum.POST_MISSION_TASK,
        MissionState.MissionStateEnum.POST_MISSION_ACTION,
      ] as ValueType<MissionState.MissionStateEnumMap>[]
    ).includes(missionState.stateEnum);
    return (
      manualTeleop ||
      (pointerLockEnabledForMissions &&
        (freeLookAllowsPanAndTilt || executingPostMissionActions) &&
        !vehicleReturning)
    );
  }
);

export const getUserCameraAutoRegionState = (state: AppState) =>
  state.pilot.userCameraAutoRegionState;

export const getLookAtFinalPointActive = (state: AppState) => state.pilot.lookAtFinalPointActive;

export const getAttachmentPortStatus = (state: AppState) => state.pilot.attachmentPortStatus;

/**
 * Calculate the height of the vehicle above the landing pad based on the nav frame positions
 * of the landing pad and the vehicle's pose.
 */
export const getCurrentHeightInFtAboveLandingPad = createSelector(
  getCurrentPose,
  getLandingPadTracker,
  (pose, landingPadTracker) => {
    // NOTE(parth): This is technically not exactly what you want -- nav_T_root is the position of the tags,
    // and nav_T_vehicle (the vehicle pose) is the position of the vehicle IMU. As a result this function will
    // return 12cm when you are on the platform. However, it looks like we are not sending the frontend
    // nav_T_vehicle_target, which is what you really want, and this is good enough for what you are using it
    // for currently
    const landingPadPosition = landingPadTracker?.focusTrack?.track?.navTRoot?.position?.dataList;
    const vehiclePosition = pose?.position;

    if (!landingPadPosition || !vehiclePosition) {
      return undefined;
    }

    const dockHeight = new THREE.Vector3().fromArray(landingPadPosition);
    const vehicleHeight = new THREE.Vector3().fromArray(vehiclePosition);

    return Math.round(metersToFeet(vehicleHeight.z - dockHeight.z));
  }
);

export const isAnyAttachmentConnected = createSelector(
  getAttachmentPortStatus,
  attachmentPortStatus => {
    return attachmentPortStatus?.portDataMap?.some(
      portData => portData[1].portState === PortState.Enum.CONNECTED
    );
  }
);
export const getSpotlightSystemStatus = (state: AppState) => state.pilot.spotlightSystemStatus;
export const getDeviceLinksSummary = (deviceId: string) =>
  createSelector(
    (state: AppState) => state.pilot.deviceLinksSummary,
    deviceLinksSummary => deviceLinksSummary[deviceId] ?? new DeviceLinksSummary()
  );
export const getDeviceNodeHealth = (deviceId: string) =>
  createSelector(
    (state: AppState) => state.pilot.deviceNodeHealth,
    deviceNodeHealth => deviceNodeHealth[deviceId] ?? new DeviceNodeHealth()
  );

export const isSpotlightAttached = createSelector(getAttachmentPortStatus, attachmentPortStatus => {
  return attachmentPortStatus?.portDataMap?.find(
    portData =>
      (
        [
          AccessoryEnum.Enum.R47_SPOTLIGHT_EVT,
          AccessoryEnum.Enum.R47_SPOTLIGHT_PVT,
        ] as ValueType<AccessoryEnum.EnumMap>[]
      ).includes(portData[1].type) && portData[1].portState === PortState.Enum.CONNECTED
  );
});

export const isSpotlightThrottled = createFaultActiveSelector(
  VehicleFault.Enum.ATTACHMENT_SPOTLIGHT_BRIGHTNESS_THROTTLING
);

export const isSpotlightOverheated = createFaultActiveSelector(
  VehicleFault.Enum.ATTACHMENT_SPOTLIGHT_ABOVE_TOUCH_TEMP
);

export const getSpeakerMicStatus = (state: AppState) => state.pilot.speakerMicStatus;

export const getSpeakerMicAudioFiles = (state: AppState) =>
  state.pilot.speakerMicAudioFiles?.getAudioFilesList();

// DroneSense Mission Selection integration
export const getDroneSenseRequestStatus = (state: AppState) =>
  state.pilot.droneSense?.requestStatus;
export const getDroneSenseMissions = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS" ? state.pilot.droneSense.missions : [];
export const getSelectedDroneSenseMissionId = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS"
    ? state.pilot.droneSense?.selectedMissionId
    : undefined;
export const getSelectedDroneSenseMission = createSelector(
  getDroneSenseMissions,
  getSelectedDroneSenseMissionId,
  (missions, selectedMissionId) => {
    return missions.find(mission => mission.missionId === selectedMissionId);
  }
);
export const getDroneSenseCallSign = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS" ? state.pilot.droneSense.callSign : "";
export const getLastKnownFlyingFlightId = (state: AppState) => state.pilot.lastKnownFlyingFlightId;

export const getFarAwayFromLaunchPoint = (state: AppState) => state.pilot.farAwayFromLaunchPoint;
export const getFarAwayFromLaunchPointDismissed = (state: AppState) =>
  state.pilot.farAwayFromLaunchPointDismissed;

export const getFlyToPointStandoffDistance = (state: AppState) =>
  state.pilot.flyToPointStandoffDistance;

export const getParachuteStatus = (state: AppState) => state.pilot.parachuteStatus;

export const getBlockingFaultsListPb = (state: AppState) => state.pilot.blockingFaultsPb;
